import { useState } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import UKLocationAutocomplete from "./UKLocationAutocomplete";
import { api, setUser } from "../lib/api";
import { toast } from "sonner";
import { Compass, MapPin, X } from "lucide-react";

/**
 * Carrier-only Service Area editor.
 *
 * Lets a licensed carrier pin a base UK location and a radius (km) — the
 * marketplace then filters waste jobs to that area, so the carrier only sees
 * jobs they can realistically fulfil.
 *
 * Props:
 *   user      current carrier user object
 *   onChange  callback invoked after a successful save with the fresh user
 */
export default function ServiceAreaCard({ user, onChange }) {
  const initialLoc = user?.service_area_lat != null && user?.service_area_lng != null && user?.service_area_label
    ? { label: user.service_area_label, lat: user.service_area_lat, lng: user.service_area_lng }
    : null;
  const [loc, setLoc] = useState(initialLoc);
  const [radius, setRadius] = useState(user?.service_area_radius_km || 25);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!loc) {
      toast.error("Pick your base UK location first.");
      return;
    }
    const r = Number(radius);
    if (!r || r < 1 || r > 500) {
      toast.error("Radius must be between 1 and 500 km.");
      return;
    }
    try {
      setSaving(true);
      const { data } = await api.patch(
        `/carriers/${user.id}/service-area`,
        { label: loc.label, lat: loc.lat, lng: loc.lng, radius_km: r },
        { params: { user_id: user.id } }
      );
      // Refresh local user object
      const fresh = {
        ...user,
        service_area_label: data.service_area_label,
        service_area_lat: data.service_area_lat,
        service_area_lng: data.service_area_lng,
        service_area_radius_km: data.service_area_radius_km,
      };
      setUser(fresh);
      onChange?.(fresh);
      toast.success(`Service area saved — you'll see jobs within ${r}km of ${loc.label}.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save service area");
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    try {
      setSaving(true);
      await api.patch(
        `/carriers/${user.id}/service-area`,
        { label: null, lat: null, lng: null, radius_km: 0 },
        { params: { user_id: user.id } }
      );
      const fresh = {
        ...user,
        service_area_label: null,
        service_area_lat: null,
        service_area_lng: null,
        service_area_radius_km: null,
      };
      setUser(fresh);
      onChange?.(fresh);
      setLoc(null);
      setRadius(25);
      toast.success("Service area cleared — you'll now see jobs UK-wide.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to clear");
    } finally {
      setSaving(false);
    }
  };

  const hasArea = user?.service_area_radius_km && user?.service_area_label;

  return (
    <Card className="p-6 rounded-2xl border-[#E2DFD8] bg-white" data-testid="service-area-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#EBE8E0] flex items-center justify-center shrink-0">
          <Compass className="w-5 h-5 text-[#1B3624]" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-medium" style={{ fontFamily: "Outfit" }}>Service area</div>
          <p className="text-xs text-[#6B6965] mt-1">
            Set where you operate so you only see jobs within your reach. Leave blank to see all UK jobs.
          </p>

          {hasArea && (
            <div
              data-testid="service-area-summary"
              className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1B3624] text-white text-xs"
            >
              <MapPin className="w-3 h-3" />
              {user.service_area_label} · {user.service_area_radius_km} km
            </div>
          )}

          <div className="mt-4 space-y-3">
            <div>
              <Label className="text-xs">Base location (UK postcode, town or city)</Label>
              <div className="mt-1">
                <UKLocationAutocomplete
                  value={loc}
                  onChange={setLoc}
                  placeholder="e.g. M1 1AE, Manchester or LS1"
                  testId="service-area-location"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Radius (km)</Label>
              <div className="mt-1 flex items-center gap-3">
                <Input
                  data-testid="service-area-radius"
                  type="number"
                  min="1"
                  max="500"
                  step="1"
                  value={radius}
                  onChange={(e) => setRadius(e.target.value)}
                  className="rounded-xl w-32"
                />
                <span className="text-xs text-[#6B6965]">1 – 500 km</span>
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <Button
                data-testid="service-area-save"
                onClick={save}
                disabled={saving}
                className="rounded-full bg-[#1B3624] hover:bg-[#274d33] text-white"
              >
                {hasArea ? "Update area" : "Save service area"}
              </Button>
              {hasArea && (
                <Button
                  data-testid="service-area-clear"
                  variant="outline"
                  onClick={clear}
                  disabled={saving}
                  className="rounded-full border-[#E2DFD8] text-[#6B6965]"
                >
                  <X className="w-3.5 h-3.5 mr-1" />
                  Clear
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
